import {
  Button,
  Icon,
  Select,
  SelectOptionType,
  showDialogConfirm,
  TextInput,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { CeSubject } from '../api';
import { ProgramFormValues, ProgramUnitSubject } from './ProgramForm';

interface ProgramCurriculumProps {
  control: Control<ProgramFormValues>;
  subjects: CeSubject[];
  selectedModality: SelectOptionType | null;
}

const ProgramCurriculum = ({
  control,
  subjects,
  selectedModality,
}: ProgramCurriculumProps) => {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'programForm.curriculum';
  const { msgValidations } = useValidations();

  const { setValue } = useFormContext<ProgramFormValues>();

  const { fields: units, append, remove } = useFieldArray({
    control,
    name: 'units',
  });

  const formValues = useWatch({ control, name: 'units' });

  // Get all already added subject IDs
  const addedSubjectIds = useMemo(() => {
    const ids = new Set<number>();
    formValues?.forEach((unit) => {
      unit.subjects?.forEach((subject) => {
        ids.add(subject.subjectId);
      });
    });
    return ids;
  }, [formValues]);

  // Filter out already added subjects
  const availableSubjects = useMemo(() => {
    return subjects.filter((s) => !addedSubjectIds.has(s.id));
  }, [subjects, addedSubjectIds]);

  const getSubjectOptions = useCallback(() => {
    return availableSubjects.map((subject) => ({
      label: `${subject.code} - ${subject.name}`,
      value: subject.id,
      modality: subject.modality?.name || '',
    }));
  }, [availableSubjects]);

  const addUnit = useCallback(() => {
    append({ name: `Unidad ${units.length + 1}`, subjects: [] });
  }, [append, units.length]);

  const removeUnit = useCallback(
    (index: number) => {
      if (units.length > 1) {
        remove(index);
      }
    },
    [remove, units.length],
  );

  const addSubjectToUnit = useCallback(
    (unitIndex: number, subjectOption: SelectOptionType) => {
      const subject = subjects.find((s) => s.id === subjectOption.value);
      if (!subject) return;

      const currentUnit = formValues[unitIndex];
      const newSubject: ProgramUnitSubject = {
        subjectId: subject.id,
        name: subject.name,
        code: subject.code,
      };

      setValue(`units.${unitIndex}.subjects`, [
        ...(currentUnit.subjects || []),
        newSubject,
      ]);
    },
    [formValues, subjects, setValue],
  );

  const openSubjectModal = useCallback(
    (unitIndex: number) => {
      const options = getSubjectOptions();
      let selectedOption: SelectOptionType | null = null;

      showDialogConfirm({
        icon: {
          name: 'information',
          color: 'primary',
        },
        title: t(`${prefix}.searchSubject`),
        children: (
          <Row>
            <Col xs={12}>
              <Select
                name="subjectSearch"
                label=""
                options={options}
                onChange={(option) => {
                  selectedOption = option as SelectOptionType | null;
                }}
                isClearable
                isSearchable
              />
            </Col>
          </Row>
        ),
        btnConfirm: {
          text: t(`common.actions.add`),
          onConfirm: () => {
            if (selectedOption) {
              addSubjectToUnit(unitIndex, selectedOption);
            }
          },
        },
        btnClose: {
          text: t(`common.actions.cancel`),
          onClose: () => {},
        },
      });
    },
    [getSubjectOptions, addSubjectToUnit, t],
  );

  const removeSubjectFromUnit = useCallback(
    (unitIndex: number, subjectIndex: number) => {
      const currentUnit = formValues[unitIndex];
      const newSubjects = [...(currentUnit.subjects || [])];
      newSubjects.splice(subjectIndex, 1);

      setValue(`units.${unitIndex}.subjects`, newSubjects);
    },
    [formValues, setValue],
  );

  const getSubjectCount = useCallback(
    (unitIndex: number) => {
      return formValues[unitIndex]?.subjects?.length || 0;
    },
    [formValues],
  );

  return (
    <div className="program-curriculum">
      {!selectedModality ? (
        <div className="alert alert-warning mb-3">
          {t(`${prefix}.selectModalityFirst`)}
        </div>
      ) : (
        <div className="semesters-list pb-4">
          {units.map((unit, unitIndex) => (
            <div
              key={unit.id}
              className="semester"
              style={{ width: '160px', marginRight: '10px' }}
            >
              <div className="semester-courses">
                <div style={{ marginBottom: '10px' }}>
                  <TextInput
                    name={`units.${unitIndex}.name`}
                    label={t(`${prefix}.unitName`, { number: unitIndex + 1 })}
                    control={control}
                    rules={{ required: msgValidations.required }}
                  />
                </div>

                <div
                  className="dashed-border"
                  style={{ width: '100%', textAlign: 'center' }}
                >
                  <div className="circle">
                    <Button
                      text=""
                      type="button"
                      size="sm"
                      icon="plus"
                      onClick={() => openSubjectModal(unitIndex)}
                    />
                  </div>
                </div>

                {formValues[unitIndex]?.subjects?.map(
                  (subject, subjectIndex) => (
                    <div
                      key={`${unitIndex}-${subject.subjectId}`}
                      className="course-box"
                      style={{
                        border: '1px solid var(--secondary)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        maxWidth: '100%',
                        width: '100%',
                        minHeight: '100px',
                      }}
                    >
                      <span className="name">{subject.name}</span>
                      <div className="course-box__info">
                        <span>Cod: {subject.code}</span>
                        <span
                          className="delete"
                          onClick={() =>
                            removeSubjectFromUnit(unitIndex, subjectIndex)
                          }
                        >
                          <Icon className="trash" name="trash" size="13px" />
                        </span>
                      </div>
                    </div>
                  ),
                )}

                {(!formValues[unitIndex]?.subjects ||
                  formValues[unitIndex]?.subjects?.length === 0) && (
                  <p
                    className="text-muted small text-center"
                    style={{ whiteSpace: 'normal' }}
                  >
                    {t(`${prefix}.noSubjectsInUnit`)}
                  </p>
                )}
              </div>

              <div
                className="credits d-flex justify-content-between align-items-center"
                style={{ width: '100%', padding: '8px 10px' }}
              >
                <span>
                  {t(`${prefix}.subjects`)}{' '}
                  <strong>{getSubjectCount(unitIndex)}</strong>
                </span>
                {units.length > 1 && (
                  <span
                    onClick={() => removeUnit(unitIndex)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Icon name="trash" size="15px" />
                  </span>
                )}
              </div>
            </div>
          ))}

          <div
            className="semester"
            style={{
              width: '160px',
              marginRight: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              className="dashed-border"
              style={{
                width: '100%',
                height: '100%',
                minHeight: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button
                text=""
                type="button"
                size="md"
                icon="plus"
                onClick={addUnit}
                outlined
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramCurriculum;
